import { ref, set, get, update, push, onValue, off, remove } from 'firebase/database';
import { db } from '../lib/firebase';
import { sendNotification } from './notificationService';
import { SessionGameState } from '../types/session-games';
import { sanitizeFirebaseKey } from '../utils/firebaseUtils';

export type SessionMaterialType = 'none' | 'news' | 'theme' | 'custom_text';
export type SessionItemType = 'word' | 'expression';
export type SessionAssignee = 'me' | 'friend' | 'both';

export interface SessionStudyMaterial {
  type: SessionMaterialType;
  title: string;
  content: string;
  topic?: string;
  themeId?: string;
  updatedBy: string;
  updatedByName: string;
  updatedAt: number;
}

export interface SessionSavedItem {
  id: string;
  word: string;
  type: SessionItemType;
  translation?: string;
  definition?: string;
  exampleSentence?: string;
  exampleTranslation?: string;
  literalMeaning?: string;
  assignedTo: SessionAssignee;
  assignedUserId: string | 'both';
  assignedUserName: string;
  addedByUserId: string;
  addedByUserName: string;
  createdAt: number;
}

export interface SessionParticipant {
  id: string;
  name: string;
  photo?: string;
  isOnline: boolean;
  lastSeen: number;
}

export interface FriendSession {
  id: string;
  status: 'pending' | 'active' | 'ended' | 'declined';
  createdAt: number;
  updatedAt: number;
  hostId: string;
  hostName: string;
  hostPhoto?: string;
  friendId: string;
  friendName: string;
  friendPhoto?: string;
  participants: Record<string, SessionParticipant>;
  studyMaterial: SessionStudyMaterial;
  savedItems?: Record<string, SessionSavedItem>;
  activeGame?: SessionGameState | null;
}

// Clean object helper to remove undefined fields for Firebase RTDB
const sanitizeForFirebase = <T extends Record<string, any>>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj, (_, v) => (v === undefined ? null : v)));
};

// Generate consistent pair key for two friends
export const getFriendPairKey = (userId1: string, userId2: string): string => {
  return [userId1, userId2].sort().join('_');
};

/**
 * Creates a new Friend Learn Session and notifies the invited friend.
 */
export const createFriendSession = async (
  host: { id: string; name: string; photo?: string },
  friend: { id: string; name: string; photo?: string },
  initialMaterial?: Partial<SessionStudyMaterial>
): Promise<string> => {
  const sessionRef = push(ref(db, 'friendSessions'));
  const sessionId = sessionRef.key!;
  const now = Date.now();

  const studyMaterial: SessionStudyMaterial = {
    type: initialMaterial?.type || 'none',
    title: initialMaterial?.title || 'Welcome to Study Session',
    content: initialMaterial?.content || 'Select a News article, Theme, or Paste your own text to start learning together!',
    topic: initialMaterial?.topic,
    themeId: initialMaterial?.themeId,
    updatedBy: host.id,
    updatedByName: host.name,
    updatedAt: now,
  };

  const newSession: FriendSession = {
    id: sessionId,
    status: 'pending',
    createdAt: now,
    updatedAt: now,
    hostId: host.id,
    hostName: host.name,
    hostPhoto: host.photo || '',
    friendId: friend.id,
    friendName: friend.name,
    friendPhoto: friend.photo || '',
    participants: {
      [host.id]: {
        id: host.id,
        name: host.name,
        photo: host.photo || '',
        isOnline: true,
        lastSeen: now,
      },
      [friend.id]: {
        id: friend.id,
        name: friend.name,
        photo: friend.photo || '',
        isOnline: false,
        lastSeen: 0,
      },
    },
    studyMaterial: sanitizeForFirebase(studyMaterial),
    savedItems: {},
  };

  // Run session initialization, user session indices,
  // and notification in parallel to eliminate sequential latency bottlenecks
  await Promise.all([
    set(sessionRef, sanitizeForFirebase(newSession)),
    set(ref(db, `userFriendSessions/${host.id}/${friend.id}`), {
      lastSessionId: sessionId,
      updatedAt: now,
      status: 'pending',
    }),
    set(ref(db, `userFriendSessions/${friend.id}/${host.id}`), {
      lastSessionId: sessionId,
      updatedAt: now,
      status: 'pending',
    }),
    sendSessionInviteNotification(sessionId, host, friend.id).catch((err) => {
      console.error('Error sending session invite notification:', err);
    }),
  ]);

  return sessionId;
};

/**
 * Sends a push notification inviting a friend to a study session.
 */
export const sendSessionInviteNotification = async (
  sessionId: string,
  host: { id: string; name: string; photo?: string },
  friendId: string
): Promise<void> => {
  const now = Date.now();
  const inviteData = {
    sessionId,
    hostId: host.id,
    hostName: host.name,
    hostPhoto: host.photo || '',
    createdAt: now,
    status: 'pending',
  };

  await Promise.all([
    set(ref(db, `userSessionInvites/${friendId}/${sessionId}`), sanitizeForFirebase(inviteData)),
    sendNotification(friendId, {
      type: 'session_invite',
      message: `${host.name} invited you to join a Study Session! 📖`,
      fromUserId: host.id,
      fromUserName: host.name,
      fromUserPhoto: host.photo,
      metadata: {
        sessionId,
        hostId: host.id,
        hostName: host.name,
      },
    }),
  ]);
};

/**
 * Accepts an invitation to join a Friend Session.
 */
export const acceptFriendSession = async (
  sessionId: string,
  user: { id: string; name: string; photo?: string }
): Promise<void> => {
  const sessionRef = ref(db, `friendSessions/${sessionId}`);
  const snap = await get(sessionRef);
  if (!snap.exists()) throw new Error('Session not found');

  const now = Date.now();
  const updates: Record<string, any> = {
    status: 'active',
    updatedAt: now,
    [`participants/${user.id}/isOnline`]: true,
    [`participants/${user.id}/lastSeen`]: now,
    [`participants/${user.id}/name`]: user.name,
  };
  if (user.photo) {
    updates[`participants/${user.id}/photo`] = user.photo;
  }

  // Update indexes and clean up invite in parallel
  const sessionData = snap.val() as FriendSession;
  await Promise.all([
    update(sessionRef, sanitizeForFirebase(updates)),
    update(ref(db, `userFriendSessions/${sessionData.hostId}/${sessionData.friendId}`), {
      status: 'active',
      updatedAt: now,
    }),
    update(ref(db, `userFriendSessions/${sessionData.friendId}/${sessionData.hostId}`), {
      status: 'active',
      updatedAt: now,
    }),
    remove(ref(db, `userSessionInvites/${user.id}/${sessionId}`)),
  ]);
};

/**
 * Declines an invitation to a Friend Session.
 */
export const declineFriendSession = async (sessionId: string): Promise<void> => {
  const sessionRef = ref(db, `friendSessions/${sessionId}`);
  const snap = await get(sessionRef);
  if (!snap.exists()) return;

  const now = Date.now();
  const sessionData = snap.val() as FriendSession;
  await Promise.all([
    update(sessionRef, {
      status: 'declined',
      updatedAt: now,
    }),
    update(ref(db, `userFriendSessions/${sessionData.hostId}/${sessionData.friendId}`), {
      status: 'declined',
      updatedAt: now,
    }),
    update(ref(db, `userFriendSessions/${sessionData.friendId}/${sessionData.hostId}`), {
      status: 'declined',
      updatedAt: now,
    }),
    remove(ref(db, `userSessionInvites/${sessionData.friendId}/${sessionId}`)),
  ]);
};

/**
 * Ends an active Friend Session.
 */
export const endFriendSession = async (sessionId: string): Promise<void> => {
  const sessionRef = ref(db, `friendSessions/${sessionId}`);
  const snap = await get(sessionRef);
  if (!snap.exists()) return;

  const now = Date.now();
  const sessionData = snap.val() as FriendSession;
  await Promise.all([
    update(sessionRef, {
      status: 'ended',
      updatedAt: now,
    }),
    update(ref(db, `userFriendSessions/${sessionData.hostId}/${sessionData.friendId}`), {
      status: 'ended',
      updatedAt: now,
    }),
    update(ref(db, `userFriendSessions/${sessionData.friendId}/${sessionData.hostId}`), {
      status: 'ended',
      updatedAt: now,
    }),
    remove(ref(db, `userSessionInvites/${sessionData.friendId}/${sessionId}`)),
    remove(ref(db, `userSessionInvites/${sessionData.hostId}/${sessionId}`)),
  ]);
};

/**
 * Updates participant presence / heartbeat in the session.
 */
export const updateSessionPresence = async (
  sessionId: string,
  userId: string,
  isOnline: boolean
): Promise<void> => {
  const presenceRef = ref(db, `friendSessions/${sessionId}/participants/${userId}`);
  await update(presenceRef, {
    isOnline,
    lastSeen: Date.now(),
  });
};

/**
 * Real-time listener for a Friend Session room.
 */
export const listenToFriendSession = (
  sessionId: string,
  callback: (session: FriendSession | null) => void
): (() => void) => {
  const sessionRef = ref(db, `friendSessions/${sessionId}`);
  const unsubscribe = onValue(sessionRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      callback({
        ...data,
        id: sessionId,
      });
    } else {
      callback(null);
    }
  });

  return () => {
    off(sessionRef);
  };
};

/**
 * Updates the study material in real time for both friends.
 */
export const updateSessionStudyMaterial = async (
  sessionId: string,
  material: Omit<SessionStudyMaterial, 'updatedAt'>
): Promise<void> => {
  const materialRef = ref(db, `friendSessions/${sessionId}/studyMaterial`);
  const sessionUpdateRef = ref(db, `friendSessions/${sessionId}`);
  const now = Date.now();

  const payload: SessionStudyMaterial = {
    ...material,
    updatedAt: now,
  };

  await Promise.all([
    set(materialRef, sanitizeForFirebase(payload)),
    update(sessionUpdateRef, { updatedAt: now }),
  ]);
};

/**
 * Saves a word or expression to the session AND automatically syncs
 * to the assigned user's personal savedWords/savedExpressions in Firebase RTDB.
 */
export const saveWordToSession = async (
  sessionId: string,
  item: Omit<SessionSavedItem, 'id' | 'createdAt'>,
  participants: { hostId: string; hostName: string; friendId: string; friendName: string }
): Promise<SessionSavedItem> => {
  const safeKey = sanitizeFirebaseKey(item.word);
  const itemRef = ref(db, `friendSessions/${sessionId}/savedItems/${safeKey}`);
  const now = Date.now();

  const savedItem: SessionSavedItem = {
    ...item,
    id: safeKey,
    createdAt: now,
  };

  // 1. Save to session room in RTDB
  await set(itemRef, sanitizeForFirebase(savedItem));

  // 2. Save to cumulative friend vocabulary pool
  const pairKey = getFriendPairKey(participants.hostId, participants.friendId);
  const cumulativeRef = ref(db, `userFriendWords/${pairKey}/${safeKey}`);
  await set(cumulativeRef, sanitizeForFirebase({
    ...savedItem,
    lastPracticedInSession: sessionId,
    lastPracticedAt: now,
  }));

  // 3. Sync to personal vocabulary based on assignment
  const targetUserIds: string[] = [];
  if (item.assignedTo === 'both') {
    targetUserIds.push(participants.hostId, participants.friendId);
  } else if (item.assignedUserId && item.assignedUserId !== 'both') {
    targetUserIds.push(item.assignedUserId);
  } else if (item.assignedTo === 'me') {
    targetUserIds.push(item.addedByUserId);
  } else if (item.assignedTo === 'friend') {
    const otherId = item.addedByUserId === participants.hostId ? participants.friendId : participants.hostId;
    targetUserIds.push(otherId);
  }

  const personalData: any = sanitizeForFirebase({
    word: item.word,
    createdAt: now,
    lastPractice: now,
    progress: 0,
    isAutoAdded: false,
    translation: item.translation || '',
    definition: item.definition || '',
    exampleSentence: item.exampleSentence || '',
    exampleTranslation: item.exampleTranslation || '',
    literalMeaning: item.literalMeaning || '',
    type: item.type === 'expression' ? 'expression' : 'single_word',
    addedFromFriendSession: sessionId,
    addedBy: item.addedByUserName,
  });

  const syncPromises = targetUserIds.map(async (targetUid) => {
    const targetPath = item.type === 'expression'
      ? `savedExpressions/${targetUid}/${safeKey}`
      : `savedWords/${targetUid}/${safeKey}`;
    
    // Check if word already exists to avoid overwriting progress
    const existingSnap = await get(ref(db, targetPath));
    if (existingSnap.exists()) {
      const existing = existingSnap.val();
      await update(ref(db, targetPath), {
        lastPractice: now,
        translation: existing.translation || personalData.translation,
        exampleSentence: existing.exampleSentence || personalData.exampleSentence,
      });
    } else {
      await set(ref(db, targetPath), personalData);
    }
  });

  await Promise.all(syncPromises);

  return savedItem;
};

/**
 * Removes a word or expression from the active session.
 */
export const removeWordFromSession = async (
  sessionId: string,
  wordKey: string
): Promise<void> => {
  const safeKey = sanitizeFirebaseKey(wordKey);
  const itemRef = ref(db, `friendSessions/${sessionId}/savedItems/${safeKey}`);
  await remove(itemRef);
};

/**
 * Retrieves all cumulative words saved between two friends across past sessions.
 */
export const getCumulativeFriendWords = async (
  userId1: string,
  userId2: string
): Promise<SessionSavedItem[]> => {
  const pairKey = getFriendPairKey(userId1, userId2);
  const snap = await get(ref(db, `userFriendWords/${pairKey}`));
  if (!snap.exists()) return [];

  const data = snap.val() || {};
  const list = Object.values(data) as SessionSavedItem[];
  return list.sort((a, b) => {
    const timeA = (a as any).lastPracticedAt || a.createdAt || 0;
    const timeB = (b as any).lastPracticedAt || b.createdAt || 0;
    return timeB - timeA;
  });
};

/**
 * Retrieves active or latest session metadata between two friends.
 */
export const getActiveFriendSession = async (
  userId: string,
  friendId: string
): Promise<{ lastSessionId: string; status: string; updatedAt: number } | null> => {
  const snap = await get(ref(db, `userFriendSessions/${userId}/${friendId}`));
  if (!snap.exists()) return null;
  return snap.val();
};

/**
 * Listens for incoming pending session invitations for a given user.
 * Uses the fast, direct per-user index `userSessionInvites/${userId}`
 * to avoid downloading the entire `friendSessions` database tree and eliminate lag.
 */
export const listenForPendingSessionInvites = (
  userId: string,
  callback: (invites: Array<{ sessionId: string; hostId: string; hostName: string; hostPhoto?: string }>) => void
): (() => void) => {
  const userInvitesRef = ref(db, `userSessionInvites/${userId}`);
  const unsubscribe = onValue(userInvitesRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const data = snapshot.val() || {};
    const pendingInvites: Array<{ sessionId: string; hostId: string; hostName: string; hostPhoto?: string }> = [];

    Object.entries<any>(data).forEach(([sessionId, s]) => {
      if (s && s.status === 'pending') {
        pendingInvites.push({
          sessionId: s.sessionId || sessionId,
          hostId: s.hostId,
          hostName: s.hostName,
          hostPhoto: s.hostPhoto,
        });
      }
    });

    callback(pendingInvites);
  });

  return () => {
    if (typeof unsubscribe === 'function') {
      unsubscribe();
    } else {
      off(userInvitesRef);
    }
  };
};
